import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "accordion", 
    "section", 
    "header", 
    "content",
    "icon"
  ]

  connect() {
    this.initializeAccordion()
  }
  
  initializeAccordion() {
    // Initialize accordion state
    this.currentStep = 1
    this.totalSteps = this.hasSectionTarget ? this.sectionTargets.length : 4
    
    // Make sure all required DOM elements are loaded
    setTimeout(() => {
      this.updateUI()
      // Force redraw of form content
      window.dispatchEvent(new Event('resize'))
    }, 100)
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++
      this.updateUI()
      // Scroll to the newly opened section
      this.scrollToCurrentSection()
      
      // Force redraw of form content in the newly opened section
      window.dispatchEvent(new Event('resize'))
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--
      this.updateUI()
      // Scroll to the newly opened section
      this.scrollToCurrentSection()
      
      // Force redraw of form content in the newly opened section
      window.dispatchEvent(new Event('resize'))
    }
  }
  
  toggleStep(event) {
    const headerElement = event.currentTarget
    const stepNumber = parseInt(headerElement.dataset.step)
    
    if (this.currentStep === stepNumber) {
      // Allow toggle behavior to collapse/expand current section
      const contentElement = this.sectionTargets[stepNumber - 1].querySelector('[data-product-accordion-target="content"]')
      const iconElement = this.sectionTargets[stepNumber - 1].querySelector('[data-product-accordion-target="icon"]')
      
      if (contentElement.classList.contains('hidden')) {
        contentElement.classList.remove('hidden')
        iconElement.classList.add('rotate-180')
      } else {
        contentElement.classList.add('hidden')
        iconElement.classList.remove('rotate-180')
      }
      return
    }
    
    this.currentStep = stepNumber
    this.updateUI()
    
    // Force redraw of form content in the newly opened section
    window.dispatchEvent(new Event('resize'))
  }
  
  scrollToCurrentSection() {
    if (this.currentStep > 0 && this.currentStep <= this.sectionTargets.length) {
      const currentSection = this.sectionTargets[this.currentStep - 1]
      if (currentSection) {
        setTimeout(() => {
          currentSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }

  updateUI() {
    // Make sure all targets are available
    if (!this.hasSectionTarget) {
      console.warn("No section targets found")
      return
    }
    
    // Update all sections
    this.sectionTargets.forEach((section, index) => {
      const stepNumber = index + 1
      
      // Find elements within this section
      const headerElement = section.querySelector('[data-product-accordion-target="header"]')
      const contentElement = section.querySelector('[data-product-accordion-target="content"]')
      const iconElement = section.querySelector('[data-product-accordion-target="icon"]')
      
      if (!headerElement || !contentElement || !iconElement) {
        console.warn(`Missing required elements for step ${stepNumber}`)
        return
      }
      
      if (stepNumber < this.currentStep) {
        // Completed step
        headerElement.classList.remove("text-gray-500", "bg-gray-100", "dark:bg-gray-800", "dark:text-gray-400", "bg-info", "text-white")
        headerElement.classList.add("bg-success", "text-white")
        
        const circleElement = headerElement.querySelector("div > div")
        if (circleElement) {
          circleElement.classList.remove("bg-gray-300", "text-gray-700", "dark:bg-gray-700", "dark:text-gray-300")
          circleElement.classList.add("bg-white", "text-success")
          
          // Update number to checkmark for completed steps
          circleElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>`
        }
        
        // Close content
        contentElement.classList.add("hidden")
        iconElement.classList.remove("rotate-180")
      } 
      else if (stepNumber === this.currentStep) {
        // Current step
        headerElement.classList.remove("text-gray-500", "bg-gray-100", "dark:bg-gray-800", "dark:text-gray-400", "bg-success")
        headerElement.classList.add("bg-info", "text-white")
        
        const circleElement = headerElement.querySelector("div > div")
        if (circleElement) {
          circleElement.classList.remove("bg-gray-300", "text-gray-700", "dark:bg-gray-700", "dark:text-gray-300")
          circleElement.classList.add("bg-white", "text-info")
          
          // If it's a number instead of a checkmark, ensure it shows the number
          if (circleElement.innerHTML.indexOf("svg") === -1) {
            circleElement.textContent = stepNumber
          }
        }
        
        // Open content
        contentElement.classList.remove("hidden")
        iconElement.classList.add("rotate-180")
      }
      else {
        // Future step
        headerElement.classList.remove("bg-info", "text-white", "bg-success")
        headerElement.classList.add("text-gray-500", "bg-gray-100", "dark:bg-gray-800", "dark:text-gray-400")
        
        const circleElement = headerElement.querySelector("div > div")
        if (circleElement) {
          circleElement.classList.remove("bg-white", "text-info", "text-success")
          circleElement.classList.add("bg-gray-300", "text-gray-700", "dark:bg-gray-700", "dark:text-gray-300")
          
          // Ensure it shows the number
          circleElement.textContent = stepNumber
        }
        
        // Close content
        contentElement.classList.add("hidden")
        iconElement.classList.remove("rotate-180")
      }
    })
  }
} 
